/**
 * Shrinking a picture before it is sent (D73).
 *
 * A phone camera produces four or five megabytes. Nobody needs that to recognise a staircase,
 * and a physician opening their travel page on hospital wifi needs it least of all. The browser
 * already has a decoder and a canvas, so the resize happens here: the API then stores what it
 * is given and never needs an image library, a native dependency or a build step for one.
 */

/** Long edge, in pixels. Enough to fill a laptop window and to pinch into on a phone. */
export const MAX_EDGE = 1600;

/** The API refuses anything over 3MB; this keeps a normal picture an order of magnitude under. */
const QUALITY = 0.82;

export interface Shrunk {
  /** A data URL, ready to POST. */
  file: string;
  width: number;
  height: number;
  bytes: number;
}

export async function shrinkImage(file: File): Promise<Shrunk> {
  if (!/^image\/(jpeg|png|webp|heic|heif)$/i.test(file.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) {
    throw new Error('That is not a picture. JPEG, PNG and WebP work.');
  }
  const bitmap = await loadBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error("This browser wouldn't resize the picture. Try a different one.");
  ctx.drawImage(bitmap, 0, 0, width, height);
  if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close();

  // JPEG for a photograph; it is what these are, and PNG would be several times the size.
  const file64 = canvas.toDataURL('image/jpeg', QUALITY);
  const bytes = Math.round(((file64.length - (file64.indexOf(',') + 1)) * 3) / 4);
  return { file: file64, width, height, bytes };
}

/**
 * `createImageBitmap` is the fast path and honours the camera's rotation flag; the <img>
 * fallback is for browsers that do not have it. Either way the file is decoded, never trusted
 * to be what its name says.
 */
async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
    } catch {
      // A format this browser cannot decode — fall through and let <img> say so.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("That picture couldn't be read. JPEG, PNG and WebP work."));
      img.src = url;
    });
  } finally {
    // Revoked after the promise settles: decoding is done by then, and leaving it would leak.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

export function sizeLabel(bytes: number): string {
  return bytes >= 1_000_000 ? `${(bytes / 1_000_000).toFixed(1)}MB` : `${Math.max(1, Math.round(bytes / 1000))}KB`;
}
